import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { BASE_URL } from "../config";

const baseUrl = BASE_URL + "module";

// 📌 Create Module (POST)
export const createModule = createAsyncThunk(
  "module/create",
  async (moduleData, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${baseUrl}/create?is_upload=s3`, moduleData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error creating module");
    }
  }
);

// 📌 Update Module (PUT)
export const updateModule = createAsyncThunk(
  "module/update",
  async (moduleData, { rejectWithValue }) => {
    try {
      const response = await axios.put(`${baseUrl}/update/${moduleData.get('module_id')}?is_upload=s3`, moduleData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error updating module");
    }
  }
);

// 📌 Get All Modules (GET)
export const getAllModules = createAsyncThunk(
  "module/getAll",
  async ({ limit = 100, offset = 0 }, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${baseUrl}/get-all?limit=${limit}&offset=${offset}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error fetching modules");
    }
  }
);


// 📌 Get Module by ID (GET)
export const getModuleById = createAsyncThunk(
  "module/getModuleById",
  async ({ id }, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${baseUrl}/get-by-id/${id}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error fetching modules");
    }
  }
);

// 📌 Get All Unlink Modules (GET)
export const getAllUnlinkModules = createAsyncThunk(
  "module/getAllUnlink",
  async ({searchQuery}, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${baseUrl}/get-unlink-module?${searchQuery}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error fetching modules");
    }
  }
);

// 📌 Fetch Procedure Module (GET)
export const fetchProcedureModule = createAsyncThunk(
  "module/fetchProcedureModule",
  async ({ procedure_id_ref }, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${baseUrl}/procedure-mapping?procedure_id_ref=${procedure_id_ref}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error fetching procedure module");
    }
  }
);

// 📌 Delete Module (DELETE)
export const deleteModule = createAsyncThunk(
  "module/delete",
  async (id, { rejectWithValue }) => {
    try {
      await axios.delete(`${baseUrl}/delete/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error deleting module");
    }
  }
);

// 🛠 Module Slice
const moduleSlice = createSlice({
  name: "module",
  initialState: {
    modules: [],
    unlinkModules: [],
    unlkmdlPagination:{},
    loading: false,
    error: null,
    procedures: [],
    procedureModule: null
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(createModule.pending, (state) => {
        state.loading = true;
      })
      .addCase(createModule.fulfilled, (state, action) => {
        state.loading = false;
        state.modules.push(action.payload);
      })
      .addCase(createModule.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload.error || "An error occurred";
      })
      .addCase(updateModule.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateModule.fulfilled, (state, action) => {
        state.loading = false;
        state.modules = state.modules.map((mod) =>
          mod.id === action.payload.id ? action.payload : mod
        );
      })
      .addCase(updateModule.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload.error || "An error occurred";
      })
      .addCase(getAllModules.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllModules.fulfilled, (state, action) => {
        state.loading = false;
        state.modules = action.payload.results;
      })
      .addCase(getAllModules.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload.error || "An error occurred";
      })

      .addCase(getAllUnlinkModules.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllUnlinkModules.fulfilled, (state, action) => {
        state.loading = false;
        state.unlinkModules = action.payload.results?.map(d=>({...d, partNumber:d.part_number, type:d.type_name}));
        state.unlkmdlPagination = action.payload.pagination
      })
      .addCase(getAllUnlinkModules.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload.error || "An error occurred";
      })

      .addCase(getModuleById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getModuleById.fulfilled, (state, action) => {
        state.loading = false;
        state.procedures = action.payload.results.procedures || [];
      })
      .addCase(getModuleById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload.error || "An error occurred";
      })

      .addCase(fetchProcedureModule.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProcedureModule.fulfilled, (state, action) => {
        state.loading = false;
        state.procedureModule = action.payload;
      })
      .addCase(fetchProcedureModule.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload.error || "An error occurred";
      })

      .addCase(deleteModule.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteModule.fulfilled, (state, action) => {
        state.loading = false;
        state.modules = state.modules.filter(
          (mod) => mod.id !== action.payload
        );
      })
      .addCase(deleteModule.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload.error || "An error occurred";
      });
  },
});

export default moduleSlice.reducer;
